import { useEffect, useState } from 'react';
import { listini, sconti } from '../../api/listini';
import type {
  CorrispondenzaLetta,
  MarcaDaScontare,
  ScontoDaScrivere,
  ScontoLetto,
} from '../../api/listini';
import { formattaPercentuale } from '../../lib/formato';
import { ImportaSconti } from './ImportaSconti';

/*  Gli sconti concordati: l'accordo commerciale, tolto dai file Excel.

    Oggi quel numero sta dentro la colonna SCONTO_LISTINO dell'ultimo file arrivato —
    BRANDONI 50, BARBERI 55, EKINEX 40, ETA 37 — uguale su tutte le righe. Vuol dire che
    la cosa che si tratta una volta l'anno e vale decine di migliaia di euro vive dentro
    dei fogli di calcolo, uno per invio, e che per cambiarla bisogna riaprire l'ultimo file
    e reimportarlo. Nessuno sa dire, guardando il gestionale, quanto sconto abbiamo su una
    marca: bisogna andare a cercare il file.

    SI PARTE DAL FORNITORE, NON DALLA MARCA

    Perché è così che si tratta: si va da Abbattista e si discute cosa ci fa su Gessi, non
    il contrario. Scelto il fornitore, l'elenco è quello delle marche che quel fornitore ci
    vende DAVVERO — una decina, non i quattrocento produttori del catalogo — con accanto
    quanti articoli sono e quale sconto portano adesso.

    Quel «adesso» è il numero scritto a mano nell'ultimo file, e proporlo è metà del
    lavoro: chi scrive la regola conferma un numero invece di ricordarselo, e vede subito
    se qualcuno l'aveva già cambiato.
*/
export function ScontiConcordati({
  modificabili,
  onChiuso,
}: {
  /** Falso per chi può solo guardare: i moduli non compaiono. */
  modificabili: boolean;
  onChiuso: () => void;
}) {
  const [righe, setRighe] = useState<ScontoLetto[] | null>(null);
  const [fornitori, setFornitori] = useState<CorrispondenzaLetta[]>([]);
  const [fornitore, setFornitore] = useState('');
  const [marche, setMarche] = useState<MarcaDaScontare[] | null>(null);
  const [bozza, setBozza] = useState<ScontoDaScrivere | null>(null);
  const [correggo, setCorreggo] = useState<string | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState<string | null>(null);

  const ricarica = () => {
    sconti
      .elenco()
      .then(setRighe)
      .catch((e: Error) => setErrore(e.message));
  };

  useEffect(() => {
    ricarica();

    /*  I fornitori da cui scegliere sono quelli delle corrispondenze: cioè esattamente
        quelli di cui importiamo un listino. Un campo di testo libero avrebbe voluto dire
        digitare «100157» a memoria, e un codice sbagliato sarebbe un accordo che non
        aggancia niente — senza dare errore.  */
    listini
      .corrispondenze()
      .then((c) => setFornitori(distinti(c)))
      .catch(() => setFornitori([]));
  }, []);

  const scegliFornitore = (codice: string) => {
    setFornitore(codice);
    setMarche(null);
    setBozza(null);
    setCorreggo(null);

    if (codice === '') return;

    sconti
      .marche(codice)
      .then(setMarche)
      .catch((e: Error) => setErrore(e.message));
  };

  const componi = (marca: MarcaDaScontare) => {
    setCorreggo(null);
    setBozza({
      codiceFornitoreMago: fornitore,
      siglaProduttore: marca.siglaProduttore,
      codiceFamigliaSconto: null,
      // Lo sconto di adesso, quando è uno solo: si conferma invece di ricordarselo.
      percentuale: marca.scontoOggi ?? 0,
      decorrenza: null,
      scadenza: null,
      nota: null,
    });
  };

  const riprendi = (riga: ScontoLetto) => {
    setCorreggo(riga.id);
    setFornitore(riga.codiceFornitoreMago);
    setMarche(null);
    setBozza({
      codiceFornitoreMago: riga.codiceFornitoreMago,
      siglaProduttore: riga.siglaProduttore,
      codiceFamigliaSconto: riga.codiceFamigliaSconto,
      percentuale: riga.percentuale,
      decorrenza: riga.decorrenza,
      scadenza: riga.scadenza,
      nota: riga.nota,
    });
  };

  const salva = (evento: React.FormEvent) => {
    evento.preventDefault();
    if (bozza === null) return;

    setInCorso('salvataggio');
    setErrore(null);

    const azione =
      correggo === null ? sconti.concorda(bozza) : sconti.correggi(correggo, bozza);

    azione
      .then(() => {
        setBozza(null);
        setCorreggo(null);
        setMarche(null);
        setFornitore('');
        ricarica();
      })
      .catch((e: Error) => setErrore(e.message))
      .finally(() => setInCorso(null));
  };

  const elimina = (riga: ScontoLetto) => {
    setInCorso(riga.id);
    setErrore(null);

    sconti
      .elimina(riga.id)
      .then(() => setRighe((r) => (r ?? []).filter((x) => x.id !== riga.id)))
      .catch((e: Error) => setErrore(e.message))
      .finally(() => setInCorso(null));
  };

  return (
    <aside className="scheda sconti-concordati">
      <header>
        <div>
          <h2>Sconti concordati</h2>
          <p className="dettaglio">Quanto ci fa ogni fornitore, su ogni marca</p>
        </div>
        <button type="button" className="chiudi" onClick={onChiuso} aria-label="Chiudi">
          ×
        </button>
      </header>

      {errore !== null && <p className="errore">{errore}</p>}

      {/*  L'import sta sopra al modulo a mano, e non in fondo, perché è il modo in cui
           la griglia si riempie la prima volta: una riga alla volta si scrive per
           correggere, non per caricare tredici fogli.  */}
      {modificabili && bozza === null && <ImportaSconti onImportato={ricarica} />}

      {modificabili && bozza === null && (
        <section className="blocco">
          <label>
            Fornitore
            <select value={fornitore} onChange={(e) => scegliFornitore(e.target.value)}>
              <option value="">Scegli…</option>
              {fornitori.map((f) => (
                <option key={f.codiceFornitoreMago} value={f.codiceFornitoreMago}>
                  {f.nomeNelFile} ({f.codiceFornitoreMago})
                </option>
              ))}
            </select>
          </label>

          {marche !== null && marche.length === 0 && (
            <p className="nota">
              Di questo fornitore non abbiamo ancora nessuna offerta aperta: importa un suo
              listino e le marche compaiono qui.
            </p>
          )}

          {marche !== null && marche.length > 0 && (
            <>
              <p className="nota">
                Le marche che questo fornitore ci vende. Lo sconto è quello che le offerte
                portano adesso, cioè il numero scritto a mano nel file.
              </p>
              <ul className="marche">
                {marche.map((m) => (
                  <li key={m.siglaProduttore}>
                    <button type="button" onClick={() => componi(m)}>
                      <span className="chiave">{m.siglaProduttore}</span>
                      <span className="nome">{m.produttore ?? '—'}</span>
                      <span className="quanti">{m.articoli} articoli</span>
                      <span className="oggi">
                        {m.scontoOggi === null
                          ? 'sconti diversi'
                          : formattaPercentuale(m.scontoOggi)}
                      </span>
                      {m.giaConcordato && <span className="segno">già concordato</span>}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}

      {bozza !== null && (
        <form className="blocco" onSubmit={salva}>
          <h3>
            {correggo === null ? 'Nuovo accordo' : 'Correggi'} · {bozza.siglaProduttore}
          </h3>

          <label>
            Sconto concordato (%)
            <input
              type="number"
              step="0.01"
              min="0.01"
              max="99.99"
              value={bozza.percentuale === 0 ? '' : bozza.percentuale}
              onChange={(e) =>
                setBozza({ ...bozza, percentuale: Number(e.target.value) })
              }
              required
            />
          </label>

          {/*  La famiglia è facoltativa e oggi resta quasi sempre vuota: nessuno dei file
               che riceviamo la porta. Sta qui perché il giorno in cui un produttore la
               manda — Gessi ha due listini con due sconti, EMPORIO al 56 e KITCHEN al
               50,5 — la regola più stretta comincia a funzionare senza toccare niente.  */}
          <label>
            Famiglia sconto <span className="facoltativo">facoltativa</span>
            <input
              value={bozza.codiceFamigliaSconto ?? ''}
              onChange={(e) =>
                setBozza({
                  ...bozza,
                  codiceFamigliaSconto: e.target.value.trim() === '' ? null : e.target.value,
                })
              }
              placeholder="vuota = tutta la marca"
              maxLength={32}
            />
          </label>

          <div className="due-campi">
            <label>
              Decorrenza
              <input
                type="date"
                value={bozza.decorrenza ?? ''}
                onChange={(e) =>
                  setBozza({ ...bozza, decorrenza: e.target.value || null })
                }
              />
            </label>

            <label>
              Scadenza
              <input
                type="date"
                value={bozza.scadenza ?? ''}
                onChange={(e) => setBozza({ ...bozza, scadenza: e.target.value || null })}
              />
            </label>
          </div>

          <label>
            Nota
            <input
              value={bozza.nota ?? ''}
              onChange={(e) => setBozza({ ...bozza, nota: e.target.value || null })}
              placeholder="accordo Bianchi maggio 2026"
              maxLength={500}
            />
          </label>

          <p className="nota">
            Da qui in poi decide questo numero, non quello scritto nel file: l'esito
            dell'importazione dirà su quante righe è successo e di quanto. Una cella sconto
            lasciata a zero nel file resta a zero — vuol dire «di questo il netto lo
            gestiamo noi».
          </p>

          <div className="comandi">
            <button type="submit" className="primario" disabled={inCorso !== null}>
              {inCorso === 'salvataggio' ? 'Salvo…' : 'Salva'}
            </button>
            <button
              type="button"
              onClick={() => {
                setBozza(null);
                setCorreggo(null);
              }}
            >
              Lascia stare
            </button>
          </div>
        </form>
      )}

      <section className="blocco">
        <h3>
          {righe === null
            ? 'Accordi'
            : righe.length === 0
              ? 'Nessun accordo scritto'
              : righe.length === 1
                ? 'Un accordo'
                : `${righe.length} accordi`}
        </h3>

        {righe === null && <p className="attesa">Lettura…</p>}

        {righe !== null && righe.length === 0 && (
          <p className="nota">
            Finché non c'è nessun accordo, gli sconti restano quelli scritti nei file: è
            come ha sempre funzionato, e scrivendone uno cambia solo la marca che nomina.
          </p>
        )}

        {righe?.map((r) => (
          <article key={r.id} className={`accordo${r.inVigore ? '' : ' scaduto'}`}>
            <div className="intestazione">
              <strong className="quanto">{formattaPercentuale(r.percentuale)}</strong>
              <span className="chi">
                {r.fornitore} · {r.siglaProduttore}
                {r.codiceFamigliaSconto !== null && ` famiglia ${r.codiceFamigliaSconto}`}
              </span>
            </div>

            <p className="dettaglio">
              {r.produttore ?? r.siglaProduttore} · {r.articoliInteressati} articoli
              {' · '}
              {periodo(r)}
              {!r.inVigore && ' · non in vigore'}
            </p>

            {r.nota !== null && <p className="nota">{r.nota}</p>}

            {modificabili && (
              <div className="comandi">
                <button type="button" onClick={() => riprendi(r)}>
                  Correggi
                </button>
                <button
                  type="button"
                  className="pericolo"
                  disabled={inCorso !== null}
                  onClick={() => elimina(r)}
                >
                  Togli
                </button>
              </div>
            )}
          </article>
        ))}
      </section>
    </aside>
  );
}

/*  Un fornitore per codice, non per nome nel file: lo stesso fornitore può comparire fra
    le corrispondenze con due nomi — «GESSI» e «GESSI EMPORIO» — e nell'elenco deve essere
    una voce sola, altrimenti si scriverebbero due accordi credendoli di due fornitori.  */
function distinti(righe: CorrispondenzaLetta[]): CorrispondenzaLetta[] {
  const visti = new Map<string, CorrispondenzaLetta>();
  for (const riga of righe) {
    if (!visti.has(riga.codiceFornitoreMago)) visti.set(riga.codiceFornitoreMago, riga);
  }
  return [...visti.values()].sort((a, b) => a.nomeNelFile.localeCompare(b.nomeNelFile));
}

function periodo(r: ScontoLetto): string {
  const da = r.decorrenza === null ? null : giorno(r.decorrenza);
  const a = r.scadenza === null ? null : giorno(r.scadenza);

  if (da === null && a === null) return 'senza scadenza';
  if (a === null) return `dal ${da}`;
  if (da === null) return `fino al ${a}`;
  return `dal ${da} al ${a}`;
}

function giorno(iso: string): string {
  // Le date arrivano come «2026-01-01»: si taglia invece di costruire una Date, che
  // sposterebbe il giorno di uno a seconda del fuso.
  const [anno, mese, giorno] = iso.slice(0, 10).split('-');
  return `${giorno}/${mese}/${anno}`;
}
